import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const achievements = [
  {
    icon: "ri-building-line",
    number: "100+",
    label: "Enterprise Clients",
    description: "Fortune 500 companies and government organizations"
  },
  {
    icon: "ri-trophy-line",
    number: "15+",
    label: "Years Experience",
    description: "Leading digital transformation and PMO initiatives"
  },
  {
    icon: "ri-team-line",
    number: "500+",
    label: "Projects Delivered",
    description: "Successful implementations across industries"
  },
  {
    icon: "ri-award-line",
    number: "98%",
    label: "Client Satisfaction",
    description: "Consistently exceeding expectations and delivering results"
  }
];

const values = [
  {
    icon: "ri-lightbulb-line",
    title: "Strategic Excellence",
    description: "We deliver sophisticated solutions that drive measurable business outcomes through strategic thinking and proven methodologies."
  },
  {
    icon: "ri-shield-check-line",
    title: "Trust & Integrity",
    description: "Building lasting partnerships through transparency, reliability, and unwavering commitment to our clients' success."
  },
  {
    icon: "ri-rocket-line",
    title: "Innovation Focus",
    description: "Combining cutting-edge technology with time-tested frameworks to create solutions that prepare businesses for the future."
  },
  {
    icon: "ri-user-heart-line",
    title: "Client-Centric",
    description: "Every engagement is tailored to your unique challenges, ensuring solutions that fit your culture and drive adoption."
  }
];

const timeline = [
  {
    year: "2008",
    title: "Foundation & Vision",
    description: "Started with a mission to bridge the gap between technology and business strategy, focusing on delivering measurable results."
  },
  {
    year: "2012",
    title: "Government Partnerships",
    description: "Expanded into government consulting, delivering large-scale digital transformation projects with complex stakeholder requirements."
  },
  {
    year: "2016",
    title: "Enterprise Growth",
    description: "Became trusted advisor to Fortune 500 companies, specializing in PMO implementation and organizational change management."
  },
  {
    year: "2020",
    title: "Digital Innovation",
    description: "Launched comprehensive digital services including advanced analytics, automation, and modern web development capabilities."
  },
  {
    year: "2024",
    title: "PM Launchpad Program",
    description: "Introduced mentorship program to develop next generation of project management leaders, sharing expertise and proven frameworks."
  }
];

export default function About() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-stone-900 mb-6">
                About KL Consulting Group
              </h1>
              <p className="text-xl md:text-2xl text-stone-600 font-light leading-relaxed mb-8">
                Delivering strategic excellence through proven methodologies, innovative solutions, and unwavering commitment to client success.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/book-consultation')}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
                >
                  Work With Us
                </button>
                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/portfolio')}
                  className="border-2 border-stone-300 text-stone-700 hover:border-stone-400 hover:text-stone-900 px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap"
                >
                  View Our Work
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/557235496742e253cefdec01575adb3c.jpg"
                  alt="KL Consulting Group Leadership"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-yellow-600 text-white p-6 rounded-2xl">
                <p className="font-bold text-2xl">15+</p>
                <p className="text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-stone-900 mb-6">
              Proven Track Record
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              Our results speak for themselves. We've built a reputation for delivering exceptional outcomes across industries and organizational scales.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div
                key={achievement.label}
                className="text-center group"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-yellow-600 group-hover:text-white transition-all duration-300">
                  <i className={`${achievement.icon} text-3xl text-yellow-600 group-hover:text-white`}></i>
                </div>
                <p className="text-4xl md:text-5xl font-bold text-stone-900 mb-2">
                  {achievement.number}
                </p>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-3">
                  {achievement.label}
                </h3>
                <p className="text-stone-600 text-sm">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-20 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20business%20founder%20portrait%20in%20sophisticated%20office%20setting%2C%20confident%20executive%20consultant%20in%20premium%20business%20attire%2C%20modern%20leadership%20photography%20with%20strategic%20planning%20background&width=600&height=750&seq=founder-portrait&orientation=portrait"
                  alt="Founder Portrait"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -top-8 -right-8 bg-yellow-600 text-white p-6 rounded-2xl">
                <i className="ri-quote-text text-3xl"></i>
              </div>
            </div>
            <div className="text-white">
              <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold mb-6">
                Leadership That Delivers
              </h2>
              <p className="text-xl text-stone-300 mb-8 leading-relaxed">
                "I founded KL Consulting Group with a simple yet ambitious vision: to bridge the gap between strategic thinking and practical implementation. Too many organizations struggle with executing their digital transformation initiatives not because they lack vision, but because they lack the frameworks and leadership to make it happen."
              </p>
              <p className="text-lg text-stone-300 mb-8 leading-relaxed">
                With over 15 years of experience leading complex initiatives across government, healthcare, financial services, and technology sectors, I've learned that success isn't just about having the right strategy—it's about having the right approach to execution, the right team dynamics, and the right change management practices.
              </p>
              <p className="text-lg text-stone-300 mb-12 leading-relaxed">
                Today, KL Consulting Group serves as a strategic partner to organizations ready to transform their operations, optimize their processes, and achieve sustainable growth through proven methodologies and innovative solutions.
              </p>
              
              <div className="border-l-4 border-yellow-600 pl-6">
                <p className="text-stone-300 italic mb-4">
                  "Every client engagement is an opportunity to create lasting impact. We don't just deliver projects—we build capabilities that endure long after our engagement ends."
                </p>
                <div>
                  <p className="font-bold text-white">Kristin L., Founder & Principal Consultant</p>
                  <p className="text-stone-400">MBA, PMP, Certified Digital Strategist</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-stone-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every solution we deliver
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="flex gap-6 group"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-600 group-hover:text-white transition-all duration-300">
                  <i className={`${value.icon} text-2xl text-yellow-600 group-hover:text-white`}></i>
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-stone-900 mb-6">
              Our Journey
            </h2>
            <p className="text-xl text-stone-600">
              From startup vision to industry leadership—a timeline of growth, innovation, and impact
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-yellow-600"></div>
            
            <div className="space-y-12">
              {timeline.map((milestone, index) => (
                <div
                  key={milestone.year}
                  className="relative flex gap-8 items-start"
                  style={{
                    animationDelay: `${index * 0.2}s`
                  }}
                >
                  <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 relative z-10">
                    {milestone.year.slice(-2)}
                  </div>
                  <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="font-['Playfair_Display'] text-2xl font-bold text-stone-900">
                        {milestone.title}
                      </h3>
                      <span className="text-yellow-600 font-bold text-lg">
                        {milestone.year}
                      </span>
                    </div>
                    <p className="text-stone-600 leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-stone-900 mb-6">
              Credentials & Expertise
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              Backed by industry-leading certifications and continuous professional development
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-stone-50 p-8 rounded-2xl text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-medal-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-4">
                Project Management
              </h3>
              <ul className="text-stone-600 space-y-2 text-sm">
                <li>• PMP Certified</li>
                <li>• Agile/Scrum Master</li>
                <li>• PRINCE2 Practitioner</li>
                <li>• Six Sigma Black Belt</li>
              </ul>
            </div>
            
            <div className="bg-stone-50 p-8 rounded-2xl text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-computer-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-4">
                Digital Strategy
              </h3>
              <ul className="text-stone-600 space-y-2 text-sm">
                <li>• Digital Transformation Leader</li>
                <li>• Google Analytics Certified</li>
                <li>• HubSpot Expert</li>
                <li>• Webflow Professional</li>
              </ul>
            </div>
            
            <div className="bg-stone-50 p-8 rounded-2xl text-center hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-stone-900 mb-4">
                Quality & Compliance
              </h3>
              <ul className="text-stone-600 space-y-2 text-sm">
                <li>• ISO 9001 Lead Auditor</li>
                <li>• ITIL Foundation</li>
                <li>• Change Management Certified</li>
                <li>• Risk Management Professional</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-stone-300 mb-12 max-w-2xl mx-auto">
            Let's discuss how our proven methodologies and strategic expertise can drive measurable results for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/book-consultation')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 rounded-full font-medium transition-all transform hover:scale-105 whitespace-nowrap"
            >
              Schedule Consultation
            </button>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/services')}
              className="border-2 border-white text-white hover:bg-white hover:text-stone-900 px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap"
            >
              Explore Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}